import React, { useEffect, useState, useCallback } from "react";
import {
  GridActionsCellItem,
  GridEditSingleSelectCell,
  GridRowModes,
  useGridApiContext,
} from "@mui/x-data-grid";
import {
  CheckSharp,
  Close,
  Delete,
  Edit,
  HorizontalRule,
  Save,
} from "@mui/icons-material";
import GridToolbar from "./grid-toolbar";
import {
  IS_HIGHRISK,
  PRODUCT_CATEGORIES,
  PRODUCT_TYPE_CATEGORIES,
  ROLES,
} from "../../constants/constant";
import { getProductType, getUserRole } from "../../utils/utils";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import ToastMessageFunction from "../toast-message-function";
import { useTranslation } from "../translation-context";
import { randomId } from "../../utils/random-id";
import { TextField } from "@mui/material";
import { getDataWAuth } from "../../utils/api-service";
import { DROPDOWNS_URI } from "../../constants/constant";
import '../../css/dm.module.css';
import CustomCountryCell from "../../components/data-management/CustomCountryCell";

function CustomSelectComponent(props) {
  const apiRef = useGridApiContext();
  const handleValueChange = async (event) => {
    await apiRef.current.setEditCellValue({
      id: props.id,
      field: "materialType",
      value: PRODUCT_TYPE_CATEGORIES[event.target.value],
    });
  };
  return (
    <GridEditSingleSelectCell {...props} onValueChange={handleValueChange} />
  );
}

function DMGrid(props) {
  const { translate: t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  const [editedRow, setEditedRow] = useState(null);
  const userData = useSelector((state) => state.userState.userData);
  const toastManager = ToastMessageFunction();
  const { errorBaseCode, row } = props;
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    setRows((prevRows) => {
      const newRow = prevRows.find(r => r.isNew === true);
      
      if (newRow) {
        return [newRow, ...row];
      }
      
      return row;
    });
  }, [row]);

  useEffect(() => {
    getDataWAuth(DROPDOWNS_URI)
      .then((res) => {
        if (res?.data && props.dmView !== "roleManagement") {
          const countryCodes = Object.values(res.data.country);
          setCountries(countryCodes);
        }else if(res?.data && props.dmView === "roleManagement"){     
          setCountries(res.data.country);
        }
      })
      .catch((error) => {
        console.error("Error fetching countries:", error);
      });
  }, [userData]);

  const handleRowModesModalChange = (newRowModesModal) =>
    setRowModesModel(newRowModesModal);

  const handleCloseClick = (id) => () => {
    if (id) {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
      const editedRow = rows.find((row) => row.id === id);
      if (editedRow.isNew) {
        setRows(rows.filter((row) => row.id !== id));
      }
      setIsInEditMode(false);
    }
  };

  const onProcessRowUpdateError = (e) => {
    console.log("onProcessRowUpdateError : ", e);
    showToast(e.message || t("saveFailed") || "Failed to save changes", "error");
    // Remove the new row if it failed to create
    setRows((prevRows) => prevRows.filter((row) => !row.isNew || row.id !== editedRow));
    setIsInEditMode(false);
    setEditedRow(null);
  };

  const processRowUpdate = async (newRow) => { 
  if (newRow.id === editedRow) {
    if (newRow.baseCode && newRow.productCategory && newRow.materialType) {
      const updatedRow = {
        baseCode: newRow.baseCode,
        productCategory: newRow.productCategory,
        materialType: newRow.materialType,
      };
      await props.create(updatedRow);
      newRow.isNew = false;
    } else if (newRow.customerNumber) {
      const updatedRow = {
        customerNumber: newRow.customerNumber,
        email: newRow.email,
        country: newRow.country,
        highRiskDealer: newRow.highRiskDealer,
      };
      if (newRow.isNew) {
        await props.create(updatedRow);
      } else {
        await props.edit(updatedRow, newRow.id);
      }
      newRow.isNew = false;
    } else if (newRow.serialNo) {
      const updatedRow = {
        serialNo: newRow.serialNo,
        comments: newRow.comments,
         country: newRow.country,
      };
      if (newRow.isNew) {
        await props.create(updatedRow);
      } else {
        updatedRow.machineId = newRow.id;
        await props.edit(updatedRow);
      }
      newRow.isNew = false;
    } else if (newRow.countries) {              
      const updatedRow = {
        id: newRow.id,
        countries: newRow.countries,
      };                
      if (newRow.isNew) {
        await props.create(updatedRow);
      } else {
        await props.edit(updatedRow);
      }
      newRow.isNew = false;
    } else if (newRow.directDebitCustomerNumber) {
      const updatedRow = {
        directDebitCustomerNumber: newRow.directDebitCustomerNumber,
        country: newRow.country,
      };
      if (newRow.isNew) {
        await props.create(updatedRow);
      } else {
        await props.edit(updatedRow, newRow.id);
      }
      newRow.isNew = false;
    }
    
    // Update the row state to reflect the changes and exit edit mode
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === newRow.id ? { ...newRow, isEdit: false } : row))
    );
  }
  
  return newRow; // Important: return the updated row
};
 const handleSaveClick = (id) => () => {
    if (id) {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View },
      });
      setRows((prevRows) =>
        prevRows.map((r) => (r.id === id ? { ...r, ...rows, isEdit: false } : r))
      );
      setIsInEditMode(false);
    }
  };

  const handleDeleteClick = (id) => () => {
    props.delete(id);
  };

const handleEditClick = (id) => () => {  
  if (id) {
    // First, close any currently editing row and reset all rows to view mode
    const updatedRowModesModel = { ...rowModesModel };
    
    // Set all rows to view mode first
    Object.keys(updatedRowModesModel).forEach(rowId => {
      updatedRowModesModel[rowId] = { mode: GridRowModes.View };
    });
    
    // Now set the clicked row to edit mode
    updatedRowModesModel[id] = { mode: GridRowModes.Edit };
    
    setRowModesModel(updatedRowModesModel);
    
    // Update rows state - set ALL rows to non-edit first, then set the target row to edit
    setRows((prevRows) =>
      prevRows.map((row) => 
        row.id === id ? { ...row, isEdit: true } : { ...row, isEdit: false }
      )
    );
  
    
    setIsInEditMode(true);
    setEditedRow(id);
  }
};

  const actions = {
    field: "actions",
    type: "actions",
    renderHeader: () => {
      return <strong>Actions</strong>;
    },
    width: 150,
    renderCell: ({ row }) => {
      return isInEditMode &&
        editedRow === row.id &&
        rowModesModel[row.id] &&
        rowModesModel[row.id]?.mode === "edit" ? (
        <div className="grid-actions">
          <GridActionsCellItem
            icon={<Close />}
            label="close"
            sx={{ color: "primary.main" }}
            onClick={handleCloseClick(row.id)}
          />
          <GridActionsCellItem
            icon={<Save />}
            label="save"
            sx={{ color: "primary.main" }}
            onClick={handleSaveClick(row.id)}
          />
        </div>
      ) : (
        <div className="grid-actions">         
          {props.dmView === "dealers" || props.dmView === "machines" || props.dmView === "roleManagement" ? (
            <GridActionsCellItem
              icon={<Edit />}
              label="edit"
              sx={{ color: "primary.main" }}
              onClick={handleEditClick(row.id)}
            />
          ) : null}
          { props.dmView !== "roleManagement" ? (
          <GridActionsCellItem
            icon={<Delete />}
            label="delete"
            sx={{ color: "primary.main" }}
            onClick={handleDeleteClick(row.id)}
          />
          ) : null}
        </div>
      );
    },
  };

   const renderEditInputCell = useCallback((params) => {
    return  (
      <TextField
        defaultValue={params.value}
        onBlur={(event) =>
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: event.target.value,
          })
        }
        variant="standard"
        fullWidth
        autoFocus={params.hasFocus}
      />
    ) ;
  },[]);

  // const renderEditSelectCell = (params, valueOptions) => {
  //   return isInEditMode && editedRow === params.id ? (
  //     <Select
  //       value={params.value}
  //       onChange={(event) =>
  //         params.api.setEditCellValue({
  //           id: params.id,
  //           field: params.field,
  //           value: event.target.value,
  //         })
  //       }
  //       fullWidth
  //       autoFocus
  //     >
  //       {valueOptions.map((country) => (
  //         <Option key={country} value={country}>
  //           {country}
  //         </Option>
  //       ))}
  //     </Select>
  //   ) : (
  //     params.value
  //   );
  // };

  const dealerColumns = [
    {
      field: "customerNumber",
      renderHeader: () => {
        return <strong>Customer Number</strong>;
      },
      width: 200,
      editable: true,
      renderEditCell: renderEditInputCell,
    },
    {
      field: "email",
      renderHeader: () => {
        return <strong>Email</strong>;
      },
      width: 250,
      editable: true,
      preProcessEditCellProps: (params) => {
        const emails = params.props.value
          .split(",")
          .map((email) => email.trim());
        if (emails.length > 10) {
          showToast(t("emailValidation"), "error");
          return { ...params.props, error: true };
        }
        return { ...params.props, error: false };
      },
      renderEditCell: renderEditInputCell,
    },
    {
      field: "modifiedBy",
      renderHeader: () => {
        return <strong>Modified By</strong>;
      },
      width: 100,
      editable: false,
    },
    {
      field: "modifiedDate",
      renderHeader: () => {
        return <strong>Modified Date</strong>;
      },
      width: 200,
      editable: false,
    },
    {
      field: "country",
      renderHeader: () => {
        return <strong>Country</strong>;
      },
      width: 150,
      editable: true,
      type: "singleSelect",
      valueOptions: countries,
    },
    {
      field: "highRiskDealer",
      renderHeader: () => {
        return <strong>Exclusion Dealer</strong>;
      },
      width: 150,
      editable: getUserRole(userData) === ROLES.CCM ? true : false,
      type: "singleSelect",
      valueOptions: IS_HIGHRISK,
      renderEditCell: (params) => {
        let value = params.value;
        if (value === true || value === 'Yes') value = 'Yes';
        else value = 'No';
        return (
          <GridEditSingleSelectCell
            {...params}
            value={value}
            valueOptions={IS_HIGHRISK}
          />
        );
      },
      renderCell: (params) => {
        const value = params.row.highRiskDealer;
        if (value === true || value === 'Yes') {
          return <CheckSharp sx={{ color: "#367C2B" }} />;
        } else {
          return <HorizontalRule />;
        }
      },
    },
  ];

  const productColumns = [
    {
      field: "baseCode",
      renderHeader: () => {
        return <strong>Base Code</strong>;
      },
      width: 200,
      editable: true,
      renderEditCell: renderEditInputCell,
    },
    {
      field: "productCategory",
      renderHeader: () => {
        return <strong>Product Category</strong>;
      },
      width: 200,
      editable: true,
      type: "singleSelect",
      valueOptions: PRODUCT_CATEGORIES,
      renderEditCell: (params) => <CustomSelectComponent {...params} />,
    },
    {
      field: "materialType",
      renderHeader: () => {
        return <strong>Material Type</strong>;
      },
      width: 200,
      editable: true,
      valueOptions: ({ row }) => {
        if (!row) {
          // return PRODUCT_TYPE_CATEGORIES;
        }
        return getProductType(row.productCategory);
      },
    },
  ];

  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const machineColumns = [
    {
      field: "serialNo",
      renderHeader: () => {
        return <strong>Serial Number</strong>;
      },
      width: 200,
      editable: true,
      renderEditCell: renderEditInputCell,
    },
    {
      field: "comments",
      renderHeader: () => {
        return <strong>Comments</strong>;
      },
      width: 350,
      editable: true,
      renderEditCell: renderEditInputCell,
    },
    {
      field: "modifiedBy",
      renderHeader: () => {
        return <strong>Modified By</strong>;
      },
      width: 110,    
      editable: false, 
    },{
      field: "dateOfInsertion",
      renderHeader: () => {
        return <strong>Modified Date</strong>;
      },
      width: 220,
      editable: false,
    },{
      field: "country",
      renderHeader: () => {
        return <strong>Country</strong>;
      },
      width: 100,
      editable: true,
      type: "singleSelect",
      valueOptions: countries,
    },
  ];

  const roleManagementColumns = [
    {
      field: "userName",
      renderHeader: () => {
        return <strong>Name</strong>;
      },
      width: 200,     
    },
    {
      field: "racfID",
      renderHeader: () => {
        return <strong>RACF</strong>;
      },
      width: 200,     
    },
    {
      field: "roles",
      renderHeader: () => {
        return <strong>Role</strong>;
      },
      width: 200,      
    },

     {
      field:  'countries',  
      renderHeader: () => {
        return <strong>Countries</strong>;
      },
      width: 200,   
      editable: true,      
      // type: 'singleSelect',
     // valueOptions: props.dmView === 'roleManagement' ? Object.keys(countries) : countries,
     renderEditCell: (params) => 
     <CustomCountryCell
      {...params} 
       valueOptions={props.dmView=== 'roleManagement'? Object.keys(countries) : countries}
     isEdit={params.row.isEdit}   
     />            
    },

  ];

  const directDebitDealerColumns = [
    {
      field: "directDebitCustomerNumber",
      renderHeader: () => {
        return <strong>Customer Number</strong>;
      },
      width: 250,
      editable: true,
      renderEditCell: renderEditInputCell,
    },    
    {
      field: "country",
      renderHeader: () => {
        return <strong>Country</strong>;
      },
      width: 150,
      editable: true,
      type: "singleSelect",
      valueOptions: ["JD"],
    },
    {
      field: "modifiedBy",
      renderHeader: () => {
        return <strong>Modified By</strong>;
      },
      width: 150,
      editable: false,
    },
    {
      field: "modifiedDate",
      renderHeader: () => {
        return <strong>Modified Date</strong>;
      },
      width: 200,
      editable: false,
    },
  ];

  

  const setRowModeAsEdit = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.Edit },
    });
  };

  useEffect(() => {
    if (errorBaseCode && errorBaseCode?.baseCode) {
      const id = randomId();
      const newRow = {
        id,
        baseCode: errorBaseCode.baseCode,
        productCategory: "",
        materialType: "",
        isNew: true,
      };
      
      setRows((prev) => {
        const existingRow = prev.find(row => row.baseCode === errorBaseCode.baseCode && row.isNew);
        if (existingRow) {
          return prev;
        }
        return [newRow, ...prev];
      });
      
      setRowModeAsEdit(id);
      setIsInEditMode(true);
      setEditedRow(id);
    }
  }, [errorBaseCode]);

  const viewColumns = props.dmView === "dealers" ? dealerColumns : props.dmView === "products" ? productColumns : props.dmView === "roleManagement" ? roleManagementColumns : props.dmView === "directDebitDealers" ? directDebitDealerColumns : machineColumns;
  const columns = (getUserRole(userData) === ROLES.BP || getUserRole(userData) === ROLES.CCM || ((props.dmView === "roleManagement") && userData?.userID  === "SB04744")) ? viewColumns.concat(actions) : viewColumns;

  return (
    <div style={{ maxHeight: 600, height: 600 }}>
      {toastManager.toast}
      <DataGridPro
        loading={props.isLoaderVisible}
        rows={rows}
        columns={columns}
        rowModesModel={rowModesModel}
        handleRowModesModalChange={handleRowModesModalChange}
        processRowUpdate={processRowUpdate}
        editMode="row"
        onProcessRowUpdateError={onProcessRowUpdateError}
        pagination
        paginationMode="client"
        pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            view: props.view,
            dmView: props.dmView,
            count: props.count,
            setRows,
            setIsInEditMode,
            setRowModesModel,
            setEditedRow,
          },
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            boxShadow: "0px 8px 8px rgba(0,0,0,0.1)",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#fff",
          },
          "& .MuiDataGrid-cell": {
            backgroundColor: "#fff",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#fff",
            boxShadow: "0px 0px 8px rgba(0,0,0,0.2)",
           
          },
          "& .MuiDataGrid-filler": {
            // display: 'none',
            backgroundColor: "#fff",
          },
          boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
          borderRadius: "8px",
          padding: "4px",
          backgroundColor: "#fff",
          marginBottom: "10px",
        }}
      />
    </div>
  );
}

export default DMGrid;
